import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

import { isRetiredConcept } from "@/lib/concepts";

const STORAGE_KEY = "eventsverige.inquiry";

export interface InquiryItem {
  slug: string;
  name: string;
  image: string;
  quantity: number;
}

interface Ctx {
  items: InquiryItem[];
  count: number;
  add: (item: Omit<InquiryItem, "quantity">, qty?: number) => void;
  /** Idempotently select an item once; never increases an existing quantity. */
  ensure: (item: Omit<InquiryItem, "quantity">) => void;
  remove: (slug: string) => void;
  setQty: (slug: string, qty: number) => void;
  clear: () => void;
  has: (slug: string) => boolean;
}

/**
 * Functional state updater used by the store's `ensure` method. Selecting an
 * item that is already present returns the same state (quantity untouched).
 */
function ensureInquiryItem(item: Omit<InquiryItem, "quantity">) {
  return (prev: InquiryItem[]): InquiryItem[] => {
    if (isRetiredConcept(item.slug.replace(/^concept:/, ""))) return prev;
    return prev.some((p) => p.slug === item.slug) ? prev : [...prev, { ...item, quantity: 1 }];
  };
}

const InquiryContext = createContext<Ctx | null>(null);

export function InquiryProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<InquiryItem[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const saved: InquiryItem[] = JSON.parse(raw);
        setItems(saved.filter((item) => !isRetiredConcept(item.slug.replace(/^concept:/, ""))));
      }
    } catch {
      // Local storage may be unavailable; the in-memory inquiry remains usable.
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // Local storage may be unavailable; the in-memory inquiry remains usable.
    }
  }, [items, hydrated]);

  const add: Ctx["add"] = (item, qty = 1) => {
    if (isRetiredConcept(item.slug.replace(/^concept:/, ""))) return;
    setItems((prev) => {
      const existing = prev.find((p) => p.slug === item.slug);
      if (existing) {
        return prev.map((p) => (p.slug === item.slug ? { ...p, quantity: p.quantity + qty } : p));
      }
      return [...prev, { ...item, quantity: qty }];
    });
  };

  const ensure: Ctx["ensure"] = (item) => setItems(ensureInquiryItem(item));

  const remove: Ctx["remove"] = (slug) => setItems((prev) => prev.filter((p) => p.slug !== slug));

  const setQty: Ctx["setQty"] = (slug, qty) => {
    if (qty <= 0) return remove(slug);
    setItems((prev) => prev.map((p) => (p.slug === slug ? { ...p, quantity: qty } : p)));
  };

  const clear = () => setItems([]);
  const has = (slug: string) => items.some((p) => p.slug === slug);
  const count = items.reduce((sum, p) => sum + p.quantity, 0);

  return (
    <InquiryContext.Provider value={{ items, count, add, ensure, remove, setQty, clear, has }}>
      {children}
    </InquiryContext.Provider>
  );
}

export function useInquiry() {
  const ctx = useContext(InquiryContext);
  if (!ctx) throw new Error("useInquiry must be used within InquiryProvider");
  return ctx;
}
